// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { ShareNetwork } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2CreateProjectButton,
  canPromoteChatToProject,
} from "./AskV2CreateProjectButton";

import { ShareAskPunttChatDialog } from "../../components/ask-puntt/ShareAskPunttChatDialog";
import { AskPunttArtifactProvider } from "../../components/ask-puntt-review";
import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { buildMentionComposerMembers } from "../../utils/buildMentionComposerMembers";
import { useAppSelector } from "../../utils/hooks";
import { useMarkAskPunttChatRead } from "../../utils/queries/askPuntt";
import {
  useAskPunttProject,
  useProjectReviews,
} from "../../utils/queries/askPunttProjects";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { ASK_V2_HOME_PATH } from "../paths";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import { AskV2RightRail } from "../rail/AskV2RightRail";
import {
  collectRailContent,
  getReviewProgressMode,
  hasRailContent,
} from "../rail/collectRailContent";
import { mergeRailProgress } from "../rail/mergeRailProgress";
import { type AskV2RailSectionId } from "../rail/types";
import { useAskV2PlanProgress } from "../rail/useAskV2PlanProgress";
import { useAskV2RailReviewAutoOpen } from "../rail/useAskV2RailReviewAutoOpen";
import { AskV2ReviewWorkspace } from "../review/AskV2ReviewWorkspace";
import { useAskV2ReviewArtifactContext } from "../review/useAskV2ReviewArtifactContext";
import { useAskV2ReviewSidebarAutoCollapse } from "../review/useAskV2ReviewSidebarAutoCollapse";
import { useAskV2ReviewWorkspace } from "../review/useAskV2ReviewWorkspace";
import { AskV2ChatFileDrop } from "../transport/AskV2ChatFileDrop";
import { AskV2ThreadComposerStack } from "../transport/AskV2ThreadComposerStack";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Interactivity } from "../transport/useAskV2Interactivity";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type AskV2ChatPageProps = {
  chatId: string;
  reviewId?: string;
  commentId?: string;
};

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ChatPage({
  chatId,
  reviewId,
  commentId,
}: AskV2ChatPageProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const currentUser = useAppSelector((state) => state.auth.value);
  const currentUserId = currentUser?.userID;
  const currentUserName =
    currentUser?.name?.trim() || currentUser?.email?.trim();
  const transport = useAskV2Transport({ chatId, presentation: "chat" });
  const chat = transport.historyChat;
  const title = transport.chatTitle?.trim() || t("askV2.pages.chat.title");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const [shareOpen, setShareOpen] = useState(false);
  const workspace = useAskV2ReviewWorkspace({
    scope: { kind: "chat", chatId },
    reviewId,
    commentId,
  });
  const projectId = chat?.projectId ?? null;
  const projectQuery = useAskPunttProject(projectId);
  const project = projectQuery.data?.project;
  const reviewsQuery = useProjectReviews(projectId);
  const projectMembers = project?.members ?? [];
  const allowListMembers = chat?.allowList ?? [];
  const chatParticipants = useMemo(
    () => buildMentionComposerMembers(projectMembers, allowListMembers),
    [projectMembers, allowListMembers],
  );
  // Shared chats have more than one human commenter: the owner plus everyone
  // on the allow-list (and the project roster when the chat is project-linked).
  // Without this map the review pane falls back to the generic "Member" label
  // for every comment the viewer did not write.
  const authorNameByUserId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const member of chatParticipants) {
      const label = member.name?.trim() || member.email?.trim();
      if (member._id && label) map[member._id] = label;
    }
    // The OWNER is on neither list the map is built from: `allowList` is the
    // people they shared the chat WITH, and a chat need not be project-linked.
    // Worse, `getChatHistory` withholds identities from non-owners — a shared
    // viewer is handed `allowList: []` outright — so without this entry the
    // review pane labels every finding the owner wrote with the generic
    // "Member", on exactly the cross-user/shared surfaces the attribution map
    // exists for. `ownerName`/`ownerEmail` are populated precisely when the
    // requester is NOT the owner, which is the only case the map can be
    // missing them; the owner's own view resolves through `currentUserId`.
    // Do not overwrite a roster/allow-list label that already named them.
    const ownerId = chat?.userId;
    const ownerLabel = chat?.ownerName?.trim() || chat?.ownerEmail?.trim();
    if (ownerId && ownerLabel && !map[ownerId]) map[ownerId] = ownerLabel;
    return map;
  }, [chatParticipants, chat?.userId, chat?.ownerName, chat?.ownerEmail]);
  const isChatOwner =
    !!chat &&
    !!currentUserId &&
    chat.userId === currentUserId &&
    chat.role !== "shared";
  // Whether the server lets this requester post into the chat (owner or
  // allowList member). False for MG cross-user readers and non-member
  // viewers, whose sends the backend rejects with 403 — the composer is
  // locked before a doomed send. Fails OPEN when the field is absent
  // (older backend / cached payloads): the lock only engages on the
  // server's explicit say-so.
  //
  // Locking the composer is not enough: the review workspace renders its OWN
  // AskV2Thread out of this transport (answer forms included), so a `readOnly`
  // prop we hand the thread below never reaches that surface — and review mode
  // is reachable straight from the URL. Gate the transport once, here, instead
  // of at each render site, so no consumer of AskV2TransportApi can re-open the
  // hole by forgetting a prop. See useAskV2Interactivity for the shared
  // derivation.
  //
  // Scope: AskV2TransportApi members only. Message feedback (thumbs up/down)
  // is a write that does NOT ride the transport — ChatMessage fires
  // `submitFeedback` straight from its own mutation, and that handler refuses a
  // non-owner / non-allowList caller with 403 exactly like send/abort. So the
  // `readOnly` prop on BOTH thread render sites below (the page thread and the
  // review workspace's own feed thread) carries it instead — but NOT by
  // threading a read-only flag down to ChatMessage. `AskPunttThreadProps` has
  // no such flag, and neither that component nor ChatMessage is this PR's to
  // change. The lever is the id: ChatMessage gates `showFeedback` on a truthy
  // `chatId`, and that id feeds nothing else in the row, so AskV2Thread passes
  // `chatId={readOnly ? undefined : chatId}` (transport/Thread.tsx) and the
  // thumbs are never drawn for a viewer whose POST the backend would 403.
  //
  // The review workspace's own react-query writes never flow through the
  // transport and stay uncovered —
  // `createReviewComment` (from `useCreateAskPunttReviewComment`, behind
  // `handleSubmitHumanComment` / `handleJudgeComment` in
  // AskV2ReviewWorkspace.tsx) still POSTs for a read-only viewer who opens
  // review mode straight from the URL — and that POST SUCCEEDS. Christo
  // authorizes that route on enterprise scope alone: `createReviewComment`
  // (services/christo/app/handlers/catalyst/askPunttReview.ts) resolves the
  // review through `loadReview`, whose whole filter is
  // `{ _id: reviewId, enterpriseId }`, with no owner / allowList /
  // canInteract check anywhere in that handler; `userId` is recorded as
  // `createdBy` only, so the write is attributable but not authorized. Any
  // enterprise member can therefore comment on any review in the enterprise,
  // on or off the chat. Hiding the composer is the ONLY thing stopping a
  // read-only viewer on this surface — do not read it as server-refused.
  // The `readOnly` prop reaching AskV2ReviewWorkspace withholds those two
  // affordances (artwork human-comment composer, judge controls) alongside its
  // feed thread, so the workspace does not look locked while one pane still
  // writes. Read that as defence in depth, not as authorization: the routes
  // stay open on the server, and closing them for real needs a membership
  // check inside `createReviewComment` / `updateReviewComment`, which is not
  // in this diff.
  const { canInteract, gatedTransport } = useAskV2Interactivity(transport);
  const mentionComposerMembers = useMemo(
    () =>
      buildMentionComposerMembers(
        isChatOwner ? projectMembers : [],
        allowListMembers,
      ),
    [allowListMembers, isChatOwner, projectMembers],
  );
  const progressMode = getReviewProgressMode({
    chat: transport.historyChat,
    messages: transport.historyMessages,
    reviews: reviewsQuery.data,
  });
  const reviewInProgress = progressMode === "running";
  const chatSummonBar = (
    <AskV2ThreadComposerStack
      chatId={chatId}
      reviewInProgress={reviewInProgress}
    >
      <AskV2SummonBar
        transport={gatedTransport}
        mentionMembers={mentionComposerMembers}
        rosterMembers={isChatOwner ? projectMembers : []}
        enableDirectoryMentions={isChatOwner}
        composerAriaLabel={t("replyToChat")}
        readOnly={!canInteract}
      />
    </AskV2ThreadComposerStack>
  );
  const planProgress = useAskV2PlanProgress({
    messages: transport.historyMessages,
    mode: progressMode,
  });
  const setRailOpenStable = useCallback(
    (open: boolean) => setRailOpen(open),
    [],
  );
  const setSectionOpenStable = useCallback(
    (
      updater: (
        current: Record<AskV2RailSectionId, boolean>,
      ) => Record<AskV2RailSectionId, boolean>,
    ) => setSectionOpen(updater),
    [],
  );
  useAskV2RailReviewAutoOpen({
    mode: progressMode,
    setRailOpen: setRailOpenStable,
    setSectionOpen: setSectionOpenStable,
  });
  const railContent = useMemo(() => {
    const base = collectRailContent({
      project,
      chat: transport.historyChat,
      messages: transport.historyMessages,
      reviews: reviewsQuery.data,
      // Straight off the socket frame: the rail draws a finding the moment
      // warhol publishes it, without waiting for the next history poll.
      liveFindings: transport.streamSnapshot?.verifiedFindings,
    });
    return mergeRailProgress(base, planProgress);
  }, [
    project,
    reviewsQuery.data,
    planProgress,
    transport.historyChat,
    transport.historyMessages,
    transport.streamSnapshot,
  ]);
  const railHasContent = hasRailContent(railContent);
  const showRailToggle = !workspace.isReviewOpen && railHasContent;
  const versionOptions = useMemo(() => {
    const reviewIds = transport.historyChat?.reviewIds ?? [];
    return reviewIds.map((id, index) => ({
      reviewId: id,
      title: t("askV2.review.versionLabel", { number: index + 1 }),
    }));
  }, [transport.historyChat?.reviewIds, t]);
  const artifactContext = useAskV2ReviewArtifactContext({
    workspace,
    messages: transport.historyMessages,
  });
  useAskV2ReviewSidebarAutoCollapse(workspace.isReviewOpen);

  const markChatRead = useMarkAskPunttChatRead(chatId);
  const markChatReadMutate = markChatRead.mutate;
  const tailMessage = transport.historyMessages.at(-1);
  const tailMessageId = tailMessage?._id ?? null;
  // why: one receipt per SETTLED tail only — completion bumps lastMessageAt
  // server-side, so a receipt sent while this reader's own turn is still
  // posting, or the assistant tail is still pending, flips the chat straight
  // back to unread.
  // why-not: `transport.inFlight` — deriveInFlight() calls any outstanding
  // user-role tail in flight (warhol writes no pending assistant row), so a
  // chat whose last persisted message is a user turn (an aborted or errored
  // generation, an @mention nobody answered) would read as permanently in
  // flight and never earn a receipt however often it was opened.
  const replyOutstanding =
    transport.sending ||
    (tailMessage?.role === "assistant" && tailMessage.status === "pending");
  const tailReadReceiptKey = chat
    ? `${tailMessageId ?? "chat"}:${chat.lastMessageAt ?? ""}`
    : null;
  const lastMarkedReadReceiptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tailReadReceiptKey || replyOutstanding) return;
    // why: a receipt is only honest once the reader has actually LOOKED at the
    // chat. Mounting is not looking: a chat opened in a background tab (the
    // ordinary cmd/ctrl-click out of the home @mention task list, or a restored
    // session) still runs its history query, because react-query only gates
    // refetchInterval/refetchOnWindowFocus on visibility, never the first
    // fetch. Marking read from there is not merely a cosmetic dot: christo's
    // markChatRead also runs clearMentionNotificationsForChat, so the home task
    // and the notification for a message nobody has read are destroyed behind
    // the reader. Hold the receipt while the document is hidden and send it on
    // the visibilitychange that brings them here — the same gate the v1 route
    // has had all along (routes/ask-puntt/chat.tsx).
    const markWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (lastMarkedReadReceiptRef.current === tailReadReceiptKey) return;
      lastMarkedReadReceiptRef.current = tailReadReceiptKey;
      markChatReadMutate(
        tailMessageId ? { messageId: tailMessageId } : undefined,
      );
    };
    markWhenVisible();
    // Re-runs (and so re-binds with a fresh key) whenever the tail settles or
    // advances, which is why the handler can close over the key directly
    // instead of reading it back out of a ref.
    document.addEventListener("visibilitychange", markWhenVisible);
    return () =>
      document.removeEventListener("visibilitychange", markWhenVisible);
  }, [markChatReadMutate, replyOutstanding, tailMessageId, tailReadReceiptKey]);

  return (
    <AskV2ChatFileDrop disabled={!canInteract}>
      <AskV2ConversationShell
        testId="ask-v2-chat-page"
        fullBleed={workspace.isReviewOpen}
        scrollResetKey={chatId}
        scrollDeps={
          workspace.isReviewOpen ? undefined : [transport.displayMessages]
        }
        autoScrollEnabled={!transport.inFlight}
        scrollSentSignal={transport.sending}
        onAddQuotedContext={
          workspace.isReviewOpen ? undefined : transport.addQuotedContext
        }
        header={
          <PageHeader>
            <BreadcrumbHeaderTrail className="min-w-0 flex-1 overflow-hidden">
              <BreadcrumbHeaderRoot
                to={ASK_V2_HOME_PATH}
                label={tCommon("nav.askPuntt")}
                testId="ask-v2-chat-breadcrumb-root"
              />
              <span
                className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
                data-testid="ask-v2-chat-title"
              >
                {title}
              </span>
              <span
                className={`${BREADCRUMB_HEADER_CHIP_CLASS} sr-only`}
                data-testid="ask-v2-chat-id"
              >
                {chatId}
              </span>
            </BreadcrumbHeaderTrail>
            {isChatOwner || showRailToggle ? (
              <Flex
                align="center"
                gap="1"
                className="ml-auto shrink-0"
                data-testid="ask-v2-chat-header-actions"
              >
                {isChatOwner && chat && canPromoteChatToProject(chat) ? (
                  <AskV2CreateProjectButton chatId={chatId} />
                ) : null}
                {isChatOwner ? (
                  <Button
                    data-testid="ask-v2-chat-share"
                    aria-label={t("shareChat")}
                    size="1"
                    variant="soft"
                    color="gray"
                    className="cursor-pointer"
                    onClick={() => setShareOpen(true)}
                  >
                    <ShareNetwork size={16} aria-hidden="true" />
                    {t("share")}
                  </Button>
                ) : null}
                {showRailToggle ? (
                  <AskV2RailToggle
                    open={railOpen}
                    onToggle={() => setRailOpen((current) => !current)}
                  />
                ) : null}
              </Flex>
            ) : null}
          </PageHeader>
        }
        composer={workspace.isReviewOpen ? undefined : chatSummonBar}
        rail={
          showRailToggle ? (
            <AskV2RightRail
              open={railOpen}
              content={railContent}
              sectionOpen={sectionOpen}
              onReviewOpen={workspace.openReview}
              onSectionOpenChange={(id, open) =>
                setSectionOpen((current) => ({ ...current, [id]: open }))
              }
            />
          ) : null
        }
      >
        {workspace.isReviewOpen ? (
          <AskV2ReviewWorkspace
            workspace={workspace}
            transport={gatedTransport}
            versionOptions={versionOptions}
            feedPresentation="chat"
            feedActorName={currentUserName}
            feedMembers={mentionComposerMembers}
            currentUserId={currentUserId}
            authorNameByUserId={authorNameByUserId}
            artifactContext={artifactContext}
            composer={chatSummonBar}
            // The workspace's own feed thread renders the per-message thumbs,
            // which POST outside the transport — the gate above cannot reach
            // them, so review mode needs the flag too.
            readOnly={!canInteract}
          />
        ) : (
          <AskPunttArtifactProvider value={artifactContext}>
            {transport.historyLoading && transport.messages.length === 0 ? (
              <AskV2ThreadLoading />
            ) : transport.historyError && transport.messages.length === 0 ? (
              <Text
                as="p"
                size="2"
                color="gray"
                data-testid="ask-v2-thread-error"
                className="py-8 text-center"
              >
                {t("askV2.pages.chat.loadError")}
              </Text>
            ) : (
              <AskV2Thread
                transport={gatedTransport}
                presentation="chat"
                feedMembers={mentionComposerMembers}
                currentUserId={currentUserId}
                // The answer form sends straight through the transport, and the
                // per-message thumbs POST `submitFeedback` outside it; both
                // need the same gate the composer gets above — otherwise a
                // read-only viewer answering a question form or rating a turn
                // still fires the 403 this page locks the composer to avoid.
                readOnly={!canInteract}
              />
            )}
          </AskPunttArtifactProvider>
        )}
      </AskV2ConversationShell>
      {chat ? (
        <ShareAskPunttChatDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          chatId={chatId}
          chatTitle={chat.title}
          members={allowListMembers}
          projectId={projectId}
          projectName={projectQuery.data?.project?.name}
          projectMembers={projectMembers}
        />
      ) : null}
    </AskV2ChatFileDrop>
  );
}
